import type { GraphemeLessonPart } from "@-/dev.improve.haniverse.lib.level";
import { drillTypeCodec, type DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";

/**
 * The logical study item a card presents. Identity, not presentation: the same item can be shown
 * more than once in a session (a card rated Again comes back), and each showing gets its own
 * {@link LearnCardPresentationId}.
 *
 * `review` carries the full drill identity — for a pronunciation drill that includes the reading's
 * tone-number pinyin, and for a word drill the sentence it is being reviewed in, because the front
 * and the back must agree on both and a rating targets exactly this reading.
 */
export type LearnCardStudyItem =
  | {
      /**
       * The sentence framing a word card this review, by its TEXT (a static-DB sentence id
       * renumbers on every static rebuild and differs offline vs online); null/absent for
       * grapheme cards.
       */
      contextSentence?: string | null;
      drillType: DrillType;
      grapheme: string;
      kind: "review";
    }
  | {
      grapheme: string;
      kind: "lesson";
      levelIndex: number;
      /** A pronunciation-level lesson (Level `2-P1`), which shares its level with the level's own. */
      part?: GraphemeLessonPart;
      /**
       * Single-reading 2-P1 slot (tone-number pinyin). Distinguishes 干 gan4 from 干 gan1 when both
       * appear as separate pronunciation lessons.
       */
      pinyin?: string;
    }
  | { kind: "slide"; slideSlug: string }
  | {
      kind: "quiz";
      quizSlug: string;
      /**
       * `lesson`: shown in the lesson that teaches it, where the first answer enrolls it.
       * `review`: due. The card and its write are the same; what follows it differs.
       */
      source: "lesson" | "review";
    };

export type LearnCardItemKind = LearnCardStudyItem["kind"];

/** A stable, human-readable key for the item — the same drill identity keys the route params carry. */
export function learnCardItemKey(item: LearnCardStudyItem): string {
  switch (item.kind) {
    case "review": {
      const sentence = item.contextSentence == null ? "" : `@${item.contextSentence}`;
      return `review:${item.grapheme}:${drillTypeCodec.encode(item.drillType)}${sentence}`;
    }
    case "lesson": {
      const part = item.part === undefined ? "" : `:${item.part}`;
      const pinyin = item.pinyin === undefined ? "" : `:${item.pinyin}`;
      return `lesson:${item.grapheme}@${item.levelIndex}${part}${pinyin}`;
    }
    case "slide": {
      return `slide:${item.slideSlug}`;
    }
    case "quiz": {
      return `quiz:${item.source}:${item.quizSlug}`;
    }
  }
}

/**
 * One SHOWING of an item within one Learn session.
 *
 * `occurrence` is assigned by whoever sequences the deck, so a grapheme that legitimately comes
 * back later in the session gets a fresh identity — every completion event (a write landing, a
 * successor becoming ready, an animation finishing) is tied to the presentation it belongs to, and
 * a stale callback from an earlier showing of the same item can be told apart and dropped.
 * `sessionId` does the same across sessions: undo, a settings change, reset, or sign-out starts a
 * new session, and results prepared for the old one are discarded.
 */
export type LearnCardPresentationId = {
  item: LearnCardStudyItem;
  occurrence: number;
  sessionId: string;
};

export function learnCardPresentationKey(id: LearnCardPresentationId): string {
  return `${id.sessionId}#${id.occurrence}:${learnCardItemKey(id.item)}`;
}

/**
 * Whether a {@link learnCardPresentationKey} presents a slide. A worklet, so the UI thread can tell
 * a card's kind from the key it already holds (a card on show can be ahead of React's promotion).
 * The session id is a UUID, so the first `#` ends it and the first `:` after it starts the item key.
 */
export function isSlideLearnCardPresentationKey(presentationKey: string): boolean {
  "worklet";
  const itemKeyStart = presentationKey.indexOf(":", presentationKey.indexOf("#")) + 1;
  return itemKeyStart > 0 && presentationKey.startsWith("slide:", itemKeyStart);
}
